import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { setTodoListNavigation } from "store/actions/todoNavigationActions";
//#endregion
//#region types
import { IconProfileInfo, User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { AllTodoListItemType, TodoListViewInnerProps } from "models/propTypes/todoPropTypes";
import { CurrencyEnums } from "models/enums";
import { Amount } from "models/utilTypes/currencyType";
import { IconDefinitionConstants, TableConstants } from "utils/constants";
import { TodoFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Icon, TableAsync, Tags, useFilterContext } from "components/common";
import { Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTodoService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ToDoListViewInner: React.FC<TodoListViewInnerProps> = (props) => {
  const { filter } = useFilterContext<TodoFormTypes.Filter>();

  const todoService = useTodoService();

  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const filterFunc = React.useCallback(props.filter, [filter]);

  const onRowClick = (
    row: AllTodoListItemType,
    index?: number,
    page?: number,
    rowsPerPage?: number,
    getApiFilter?: (page: number) => WintUtilsWebApi.Filter
  ) => {
    dispatch(
      setTodoListNavigation({
        indexToOpen: index,
        pageToOpen: page,
        numPerPage: rowsPerPage,
        loadNext: (page: number) => {
          if (getApiFilter) {
            return filterFunc(getApiFilter(page), 0).then((res) => ({
              items: res?.data.items ?? [],
              totalItems: res?.data.totalItems ?? 0,
            }));
          } else {
            return new Promise<{ items: WintSuperkollAPIModel.Todo[]; totalItems: number }>((resolve) =>
              resolve({ items: [], totalItems: 0 })
            );
          }
        },
      })
    );

    const searchParams = new URLSearchParams(location.search);
    navigate(`${UrlHelper.formatPath(`/Todo/${index}`)}?${searchParams.toString()}`, {
      state: { background: location },
    });
  };

  const getTodoType = (item: AllTodoListItemType) => {
    const icon = todoService.getTodoIconByType(item.type) ?? IconDefinitionConstants.WQuestionCircle;
    const todoTypeText = todoService.getTodoCardName(item.group, item.subType);

    return new IconProfileInfo(todoTypeText, icon, undefined);
  };

  const stateClass = (row: AllTodoListItemType) => {
    switch (row.urgency) {
      case WintSuperkollAPIModel.TodoUrgency.ToHandle:
      case WintSuperkollAPIModel.TodoUrgency.Urgent:
        return cssExports.yellowTag;
      case WintSuperkollAPIModel.TodoUrgency.Overdue:
        return cssExports.redTag;
      default:
        return cssExports.blueTag;
    }
  };

  return (
    <>
      <Grid container>
        <TableAsync<AllTodoListItemType>
          tableId={TableConstants.TodoList}
          properties={{
            type: {
              description: TranslationKeys.Common.Type,
              formatter: (todo: AllTodoListItemType) => getTodoType(todo),
              alignment: "left",
              helpText: TranslationKeys.Todos.TodoTypeHelpText,
              noWrap: true,
              className: cssExports.iconProfileContainerSecondary,
            },
            states: {
              description: TranslationKeys.Common.Status,
              formatter: (todo: AllTodoListItemType) => (
                <Tags
                  tags={[
                    <>
                      {todo.states ?? todoService.getTodoCardSubTypeName(todo.subType)}
                      {todo.urgency === WintSuperkollAPIModel.TodoUrgency.Urgent ? (
                        <>
                          &nbsp;
                          <Icon icon={IconDefinitionConstants.WExclamationCircleSmall} />
                        </>
                      ) : (
                        <></>
                      )}
                    </>,
                  ]}
                  className={stateClass(todo)}
                />
              ),
              helpText: TranslationKeys.Todos.TodoStatusHelpText,
              width: "100px",
              disableSort: true,
            },
            ownerName: {
              description: TranslationKeys.Common.Owner,
              noWrap: true,
              helpText: TranslationKeys.Todos.TodoOwnerHelpText,
              formatter: (todo: AllTodoListItemType) =>
                todo.ownerName !== undefined ? new User(todo.ownerId, todo.ownerName, true) : "",
              alignment: "center",
            },
            listDate: {
              description: TranslationKeys.Common.Created,
              formatter: (todo: AllTodoListItemType) => (todo.listDate ? new Date(todo.listDate) : undefined),
              helpText: TranslationKeys.Todos.TodoCreatedHelpText,
              width: "80px",
              noWrap: true,
            },
            counterpartName: {
              description: TranslationKeys.Common.Counterpart,
              formatter: (todo: AllTodoListItemType) =>
                todo.counterpartMapping !== undefined ? todo.counterpartMapping : <></>,
              helpText: TranslationKeys.Todos.TodoCounterpartHelpText,
              maxWidth: "80px",
              noWrap: true,
            },
            information: {
              description: TranslationKeys.Common.Information,
              formatter: (todo: AllTodoListItemType) => todo.informationMapping ?? todo.information,
              disableSort: true,
              helpText: TranslationKeys.Todos.TodoInformationHelpText,
              maxWidth: "80px",
            },
            amount: {
              description: TranslationKeys.Common.Amount,
              formatter: (todo: AllTodoListItemType) =>
                todo.amount !== undefined ? (
                  new Amount(todo.currency ?? CurrencyEnums.CurrencyCode.SEK, todo.amount)
                ) : (
                  <></>
                ),
              helpText: TranslationKeys.Todos.TodoAmountHelpText,
              noWrap: true,
              alignment: "right",
              className: cssExports.textBlack,
            },
            dueDate: {
              description: TranslationKeys.Common.Due_Date,
              formatter: (todo: AllTodoListItemType) =>
                todo.dueDate !== undefined ? new Date(todo.dueDate) : undefined,
              helpText: TranslationKeys.Todos.TodoDueDateHelpText,
              width: "80px",
              noWrap: true,
            },
            group: {
              formatter: (todo: AllTodoListItemType) => {
                if (todo.snoozed) return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Snoozed);
                else return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Open);
              },
            },
          }}
          filter={filterFunc}
          maxRowCount={10}
          pagination="advanced"
          orderByProperty="snoozed"
          orderByDescending={false}
          thenByProperty="urgency"
          thenByDescending
          rowClickHandler={onRowClick}
          reloadKey={props.reloadKey}
          disableGroupWhenOrderBy={["type", "listDate", "counterpartName", "states", "amount", "dueDate", "ownerName"]}
        />
      </Grid>
    </>
  );
};

export default ToDoListViewInner;
